import React, { useCallback, useEffect, useState } from "react";
import { useAdminApi } from "./useAdminApi";

// encyclopedia.db (LaunchBox catalog) update: paste a release .zip URL, the API
// downloads it to a temp folder and diffs it against the live file, this tab shows the
// report, and the admin either replaces the live file (optionally refreshing the cache
// entries the release made stale) or discards the download. Backend:
// /admin/encyclopedia-update (services/games/encyclopediaUpdate.ts).

const ACTIVE_STAGES = ["downloading", "extracting", "analyzing", "applying"];
const POLL_MS = 1000;

const STAGE_LABELS = {
  downloading: "Downloading",
  extracting: "Extracting",
  analyzing: "Comparing",
  ready: "Ready to apply",
  applying: "Applying",
  applied: "Applied",
  failed: "Failed",
  discarded: "Discarded",
};

const FIELD_LABELS = {
  name: "name",
  platform_id: "platform",
  release_date: "release date",
  release_year: "release year",
  country_id: "country",
  release_type_id: "release type",
  category_id: "category",
  tgdb_id: "TheGamesDB id",
  metacritic_id: "Metacritic id",
  front_image: "front cover",
  back_image: "back cover",
  label_image: "cartridge label",
  spine_image: "spine",
  barcodes: "barcodes",
  catalog: "catalog",
};

const ERROR_MESSAGES = {
  INVALID_PARAMETER: "That doesn't look like an http(s) URL.",
  ENCYCLOPEDIA_UPDATE_BUSY: "Another update is already running.",
  ENCYCLOPEDIA_UPDATE_NOT_READY: "There's no finished comparison to apply.",
  ENCYCLOPEDIA_INTEGRITY_FAILED: "The downloaded file failed SQLite's integrity check — it can't be applied.",
  ENCYCLOPEDIA_SCHEMA_CHANGED: "The database structure changed — tick the confirmation to apply anyway.",
};

const megabytes = (bytes) => (bytes == null ? "?" : `${(bytes / (1024 * 1024)).toFixed(1)} MB`);
const number = (value) => (value == null ? "—" : value.toLocaleString());
const signed = (value) => (value > 0 ? `+${value.toLocaleString()}` : value.toLocaleString());

function Tile({ label, value, sample }) {
  return (
    <div className="admin-price-bucket">
      <span className="admin-price-bucket-label">{label}</span>
      <span className="admin-price-bucket-value">{value}</span>
      {sample && <span className="admin-price-bucket-sample">{sample}</span>}
    </div>
  );
}

function Section({ title, count, children, open = false, className = "" }) {
  return (
    <details className={`admin-table glass admin-db-section ${className}`} open={open}>
      <summary className="admin-db-section-title">
        {title}
        {count != null && <span className="admin-row-id"> · {number(count)}</span>}
      </summary>
      {children}
    </details>
  );
}

function ChangeList({ changes }) {
  return (
    <ul className="admin-db-changes">
      {changes.map((change) => (
        <li key={change.field}>
          <span className="admin-db-field">{FIELD_LABELS[change.field] || change.field}</span>{" "}
          <span className="admin-db-from">{change.from ?? "—"}</span> → <span className="admin-db-to">{change.to ?? "—"}</span>
        </li>
      ))}
    </ul>
  );
}

function CacheEntryRow({ entry, children }) {
  return (
    <div className="admin-row admin-db-entry">
      <div className="admin-row-main">
        <div className="admin-source-name">{entry.cached_name || "(no name)"}</div>
        <div className="admin-source-meta">
          {entry.platform_name || "—"} · launchbox_id <code>{entry.launchbox_id}</code>
          {entry.has_metadata && <> · has curated metadata</>}
        </div>
        {children}
      </div>
      <div className="admin-row-meta">
        <span className={`admin-queue-status admin-db-status--${entry.status}`}>{entry.status}</span>
      </div>
    </div>
  );
}

// Lookup-table rows have different columns per table — shown as "name" when there is
// one, with the remaining scalar columns after it.
function rowSummary(row) {
  const { name, ...rest } = row;
  const extras = Object.entries(rest)
    .filter(([, value]) => value !== null && value !== "" && typeof value !== "object")
    .map(([key, value]) => `${key}=${value}`)
    .join(" · ");
  return name ? `${name}${extras ? ` (${extras})` : ""}` : extras;
}

function Report({ report }) {
  const { versions, integrity, schema, tables, items, reference_tables: referenceTables, cache_impact: cache } = report;
  const changedTables = tables.filter((table) => table.current !== table.new);
  const removed = cache?.affected.filter((entry) => entry.status === "removed") ?? [];
  const changed = cache?.affected.filter((entry) => entry.status === "changed") ?? [];

  return (
    <>
      <div className="admin-price-buckets">
        <Tile label="version" value={`${versions.current ?? "?"} → ${versions.new ?? "?"}`} />
        <Tile label="integrity" value={integrity === "ok" ? "ok" : "FAILED"} sample={integrity === "ok" ? null : integrity} />
        <Tile label="structure" value={schema.identical ? "identical" : "CHANGED"} />
        {items && <Tile label="items added" value={number(items.added)} />}
        {items && <Tile label="items removed" value={number(items.removed)} />}
        {items && <Tile label="items changed" value={number(items.changed)} />}
        {cache && <Tile label="cache: changed" value={number(changed.length)} sample={`of ${number(cache.cache_entries)} cached`} />}
        {cache && <Tile label="cache: removed" value={number(removed.length)} />}
      </div>

      {!schema.identical && (
        <div className="admin-table glass admin-db-warning">
          <p className="admin-status admin-error admin-status--inline">
            The structure changed. The app reads specific tables and columns — check these before applying.
          </p>
          <ul className="admin-db-changes">
            {schema.added.map((object) => <li key={`a-${object.type}-${object.name}`}>added {object.type} <code>{object.name}</code></li>)}
            {schema.removed.map((object) => <li key={`r-${object.type}-${object.name}`}>removed {object.type} <code>{object.name}</code></li>)}
            {schema.changed.map((object) => <li key={`c-${object.type}-${object.name}`}>changed {object.type} <code>{object.name}</code></li>)}
          </ul>
        </div>
      )}
      {report.items_error && <p className="admin-status admin-error">Item comparison failed: {report.items_error}</p>}
      {report.cache_impact_error && <p className="admin-status admin-error">Cache comparison failed: {report.cache_impact_error}</p>}

      {cache && (
        <>
          <Section
            title="Removed from the catalog — kept in the cache"
            count={removed.length}
            open={removed.length > 0}
            className="admin-db-section--removed"
          >
            <p className="admin-db-hint">
              These items are gone from the new release, but the app may still hold their id on users' devices, and
              this cached copy is all that still answers for it. They are kept as they are and flagged, so they show up
              here again in every future update. Their images were usually deleted from the CDN along with them.
            </p>
            {removed.length === 0 && <p className="admin-status admin-db-hint">No cached item was removed.</p>}
            {removed.map((entry) => (
              <CacheEntryRow key={entry.entry_id} entry={entry}>
                <div className="admin-db-hint">
                  {entry.removed_since_version != null && <>Flagged as removed since v{entry.removed_since_version}. </>}
                  {entry.twins.length > 0
                    ? <>Removed as a duplicate — still in the release as {entry.twins.map((id) => <code key={id}>{id}</code>).reduce((a, b) => [a, ", ", b])}.</>
                    : "Removed from the release, with no item of the same name and platform left."}
                </div>
              </CacheEntryRow>
            ))}
            {cache.orphaned_metadata_ids.length > 0 && (
              <p className="admin-status admin-error admin-status--inline admin-db-hint">
                Curated metadata for removed items (kept, never deleted automatically):{" "}
                {cache.orphaned_metadata_ids.map((id) => <code key={id}>{id} </code>)}
              </p>
            )}
          </Section>

          <Section title="Changed — refreshed in place" count={changed.length} open={changed.length > 0}>
            <p className="admin-db-hint">
              Cached entries keep the name, year, platform, images and barcodes from when they were linked — and the CDN
              deletes replaced images, so a stale entry can show no cover. Refreshing rewrites them in place (same
              product, same links). An entry whose platform changed is also unlinked from its product, for you to link again.
            </p>
            {changed.length === 0 && <p className="admin-status admin-db-hint">No cached entry changed.</p>}
            {changed.map((entry) => (
              <CacheEntryRow key={entry.entry_id} entry={entry}>
                <ChangeList changes={entry.changes} />
                {entry.changes.some((change) => change.field === "platform_id") && (
                  <div className="admin-db-hint admin-error">Platform changed — will be unlinked from its product for a manual link.</div>
                )}
              </CacheEntryRow>
            ))}
          </Section>
        </>
      )}

      {items && (
        <Section title="Items" count={items.added + items.removed + items.changed}>
          <div className="admin-price-buckets">
            {Object.entries(items.changes_by_field).map(([field, count]) => (
              <Tile key={field} label={`${FIELD_LABELS[field] || field} changed`} value={number(count)} />
            ))}
          </div>
          <h3 className="admin-db-subtitle">Added, by platform</h3>
          <div className="admin-price-buckets">
            {items.added_by_platform.map((row) => <Tile key={row.platform} label={row.platform} value={number(row.count)} />)}
          </div>
          <h3 className="admin-db-subtitle">Removed ({number(items.removed)}{items.removed > items.removed_list.length ? `, first ${items.removed_list.length}` : ""})</h3>
          {items.removed_list.map((item) => (
            <div className="admin-row" key={item.id}>
              <div className="admin-row-main">
                <div className="admin-source-name">{item.name}</div>
                <div className="admin-source-meta">
                  {item.platform || "—"} · <code>{item.id}</code>
                  {item.twins.length > 0 ? <> · duplicate of {item.twins.join(", ")}</> : <> · no twin left</>}
                </div>
              </div>
            </div>
          ))}
          <h3 className="admin-db-subtitle">Changed ({number(items.changed)}{items.changed > items.changed_sample.length ? `, first ${items.changed_sample.length}` : ""})</h3>
          {items.changed_sample.map((item) => (
            <div className="admin-row" key={item.id}>
              <div className="admin-row-main">
                <div className="admin-source-name">{item.name} <span className="admin-row-id"><code>{item.id}</code></span></div>
                <ChangeList changes={item.changes} />
              </div>
            </div>
          ))}
        </Section>
      )}

      <Section title="Lookup tables" count={referenceTables.length}>
        {referenceTables.length === 0 && <p className="admin-status">No lookup table changed.</p>}
        {referenceTables.map((table) => (
          <div className="admin-row" key={table.table}>
            <div className="admin-row-main">
              <div className="admin-source-name">{table.table} <span className="admin-row-id">+{table.added} −{table.removed}</span></div>
              <ul className="admin-db-changes">
                {table.added_rows.map((row, index) => <li key={`a${index}`}>+ {rowSummary(row)}</li>)}
                {table.removed_rows.map((row, index) => <li key={`r${index}`}>− {rowSummary(row)}</li>)}
              </ul>
            </div>
          </div>
        ))}
      </Section>

      <Section title="Row counts" count={changedTables.length}>
        {tables.map((table) => (
          <div className="admin-row admin-db-count-row" key={table.name}>
            <div className="admin-row-main admin-source-name">{table.name}</div>
            <div className="admin-row-meta">
              <span className="admin-row-id">{number(table.current)} → {number(table.new)}</span>
              {table.current != null && table.new != null && table.current !== table.new && (
                <span className="admin-db-delta">{signed(table.new - table.current)}</span>
              )}
            </div>
          </div>
        ))}
      </Section>
    </>
  );
}

// Update downloads live in the server's temp dir until applied or discarded. A server
// restart with a job pending forgets the job but not its folder (~270–550MB) — listed
// here so leftovers never pile up unseen. The current job's folder is never removable.
function TempFolders({ folders, onRemove, busy }) {
  const unused = folders.filter((folder) => !folder.in_use);
  return (
    <Section title="Temp folders on the server" count={folders.length} open={unused.length > 0}>
      {folders.length === 0 && <p className="admin-status admin-db-hint">No update temp folder on the server.</p>}
      {folders.map((folder) => (
        <div className="admin-row admin-db-count-row" key={folder.name}>
          <div className="admin-row-main">
            <div className="admin-source-name admin-db-url">{folder.name}</div>
            <div className="admin-source-meta admin-db-url">{folder.path} · {new Date(folder.modified_at).toLocaleString()}</div>
          </div>
          <div className="admin-row-meta">
            <span className="admin-row-id">{megabytes(folder.size_bytes)}</span>
            <span className={`admin-queue-status ${folder.in_use ? "admin-db-stage--ready" : "admin-db-status--removed"}`}>
              {folder.in_use ? "in use" : "leftover"}
            </span>
          </div>
        </div>
      ))}
      {unused.length > 0 && (
        <div className="admin-row">
          <div className="admin-row-main" />
          <div className="admin-row-actions">
            <button type="button" className="admin-signout-btn admin-delete-btn" onClick={onRemove} disabled={busy}>
              Delete {unused.length} leftover{unused.length === 1 ? "" : "s"}
            </button>
          </div>
        </div>
      )}
    </Section>
  );
}

function DatabaseTab({ user, onForbidden }) {
  const apiFetch = useAdminApi(user, onForbidden);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [refreshCache, setRefreshCache] = useState(true);
  const [acceptSchema, setAcceptSchema] = useState(false);

  const load = useCallback(async () => {
    try {
      setStatus(await apiFetch("/admin/encyclopedia-update"));
    } catch (err) {
      if (err.message !== "Forbidden") setError(ERROR_MESSAGES[err.message] || err.message);
    }
  }, [apiFetch]);

  useEffect(() => {
    load();
  }, [load]);

  const job = status?.job;
  const active = job && ACTIVE_STAGES.includes(job.stage);

  // Polls only while the backend is working — a finished report doesn't change.
  useEffect(() => {
    if (!active) return undefined;
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [active, load]);

  async function run(action) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
    } catch (err) {
      if (err.message !== "Forbidden") setError(ERROR_MESSAGES[err.message] || err.message);
    } finally {
      setBusy(false);
    }
  }

  function start(event) {
    event.preventDefault();
    if (!url.trim()) return;
    run(() => apiFetch("/admin/encyclopedia-update", {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ url: url.trim() }),
    }));
  }

  function apply() {
    const affected = job.report.cache_impact?.affected ?? [];
    const removed = affected.filter((entry) => entry.status === "removed").length;
    const platform = affected.filter((entry) => entry.changes.some((change) => change.field === "platform_id")).length;
    const message = `Replace the live encyclopedia.db (v${job.report.versions.current ?? "?"}) with v${job.report.versions.new ?? "?"}?`
      + (refreshCache
        ? `\n\n${affected.length - removed} changed cache entries will be rewritten in place`
          + (platform ? ` (${platform} with a new platform will be unlinked from their product)` : "")
          + `.\n${removed} removed item${removed === 1 ? "" : "s"} will be kept and flagged.`
        : "\n\nThe cache is kept as is.")
      + "\n\nThe current file is overwritten — there is no undo.";
    if (!window.confirm(message)) return;
    run(() => apiFetch("/admin/encyclopedia-update/apply", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_cache: refreshCache, accept_schema_changes: acceptSchema }),
    }));
  }

  function removeTempFolders() {
    const unused = (status?.temp_folders ?? []).filter((folder) => !folder.in_use);
    const total = unused.reduce((sum, folder) => sum + folder.size_bytes, 0);
    if (!window.confirm(`Delete ${unused.length} unused temp folder${unused.length === 1 ? "" : "s"} (${megabytes(total)})?`)) return;
    run(() => apiFetch("/admin/encyclopedia-update/temp-folders", { method: "DELETE" }));
  }

  function discard() {
    if (!window.confirm("Discard this download? The temp files are deleted and nothing changes.")) return;
    run(() => apiFetch("/admin/encyclopedia-update", { method: "DELETE" }));
  }

  const current = status?.current;
  const report = job?.report;
  const percent = job?.total_bytes ? Math.floor((job.downloaded_bytes / job.total_bytes) * 100) : null;

  return (
    <div>
      <p className="kicker">LaunchBox catalog</p>
      <h2 className="admin-title admin-title--left">Database update</h2>

      {current && (
        <div className="admin-price-buckets">
          <Tile label="live version" value={current.version ?? "?"} />
          <Tile label="size" value={megabytes(current.size_bytes)} />
          <Tile label="file date" value={current.modified_at ? new Date(current.modified_at).toLocaleDateString() : "—"} sample={current.path} />
        </div>
      )}

      <div className="admin-table glass admin-db-form">
        <form className="admin-tgdb-link-form" onSubmit={start}>
          <input
            type="url"
            placeholder="https://…/encyclopedia/v1/db191/encyclopedia.db.zip"
            className="admin-tgdb-link-input"
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            disabled={busy || active}
          />
          <button type="submit" className="admin-signout-btn" disabled={busy || active || !url.trim()}>
            Download & compare
          </button>
        </form>
        <p className="admin-db-hint">
          Downloads to a temp folder and compares it with the live file. Nothing changes until you apply.
        </p>
      </div>

      {error && <p className="admin-status admin-error">{error}</p>}

      {job && (
        <div className="admin-table glass">
          <div className="admin-row">
            <div className="admin-row-main">
              <div className="admin-source-name admin-db-url">{job.url}</div>
              <div className="admin-source-meta">
                started {new Date(job.started_at).toLocaleString()}
                {job.stage === "downloading" && <> · {megabytes(job.downloaded_bytes)} of {megabytes(job.total_bytes)}{percent != null && ` (${percent}%)`}</>}
              </div>
              {job.stage === "downloading" && percent != null && (
                <div className="admin-db-progress"><div className="admin-db-progress-bar" style={{ width: `${percent}%` }} /></div>
              )}
              {job.error && <div className="admin-status admin-error admin-status--inline">{job.error}</div>}
              {job.result && (
                <div className="admin-db-hint">
                  Replaced {job.result.replaced_path}.
                  {job.result.cache_refreshed
                    ? <>
                        {` Refreshed ${job.result.refreshed_entries} cache entries, flagged ${job.result.marked_removed} as removed.`}
                        {job.result.unlinked_for_platform_change.length > 0 && (
                          <> Unlinked for a new platform — link them in the LaunchBox tab:{" "}
                            {job.result.unlinked_for_platform_change.map((id) => <code key={id}>{id} </code>)}</>
                        )}
                      </>
                    : " Cache kept as is."}
                  {job.result.cache_error && <span className="admin-error"> Cache cleanup failed: {job.result.cache_error}</span>}
                </div>
              )}
            </div>
            <div className="admin-row-meta">
              <span className={`admin-queue-status admin-db-stage--${job.stage}`}>{STAGE_LABELS[job.stage] || job.stage}</span>
              {(active || job.stage === "ready") && job.stage !== "applying" && (
                <button type="button" className="admin-signout-btn admin-delete-btn" onClick={discard} disabled={busy}>Discard</button>
              )}
            </div>
          </div>
        </div>
      )}

      {report && job.stage === "ready" && (
        <div className="admin-table glass admin-db-apply">
          <label className="admin-checkbox-label">
            <input type="checkbox" checked={refreshCache} onChange={(event) => setRefreshCache(event.target.checked)} />
            Refresh the {number(report.cache_impact?.affected.length ?? 0)} stale cache entries
          </label>
          {!report.schema.identical && (
            <label className="admin-checkbox-label admin-error">
              <input type="checkbox" checked={acceptSchema} onChange={(event) => setAcceptSchema(event.target.checked)} />
              I checked the structure changes — apply anyway
            </label>
          )}
          <button
            type="button"
            className="admin-signout-btn admin-db-apply-btn"
            onClick={apply}
            disabled={busy || report.integrity !== "ok" || (!report.schema.identical && !acceptSchema)}
          >
            Replace live database
          </button>
        </div>
      )}

      {report && <Report report={report} />}

      {status && <TempFolders folders={status.temp_folders ?? []} onRemove={removeTempFolders} busy={busy} />}
    </div>
  );
}

export default DatabaseTab;
